import { useEffect, useRef } from 'react';
import leadBroadcastService from '../../services/leadBroadcastService';

/**
 * Mounts once per authenticated session and surfaces "your claim was
 * auto-rejected" toasts on next page load. Triggered by a lead owner
 * cancelling a broadcast — usually because they regenerated offers in
 * the wizard.
 *
 * Behavior:
 *   1. On mount, fetch up to 10 unseen notifications for this user
 *   2. For each one, fire a `window.toast.show(...)` (the global toast
 *      ref set up in App.js)
 *   3. Call mark-seen with all the IDs so they don't show again
 *
 * Uses a module-level ref to guarantee we only fire once per session,
 * even if ProtectedRoute remounts (which it does on every route change
 * in React 18 strict mode dev builds).
 */
const ClaimRejectionNotifier = () => {
    const hasRunRef = useRef(false);

    useEffect(() => {
        if (hasRunRef.current) return;
        hasRunRef.current = true;

        let cancelled = false;

        (async () => {
            try {
                const result = await leadBroadcastService.getUnseenClaimRejections();
                if (cancelled) return;
                const items = result?.data?.notifications || [];
                if (items.length === 0) return;

                // Wait a tick so the global Toast ref is mounted by App.js
                // (the ref-assignment happens during render, but our useEffect
                // could fire before window.toast is attached on the very first
                // mount of the session).
                await new Promise((r) => setTimeout(r, 250));
                if (cancelled) return;

                items.forEach((n, idx) => {
                    const triggeredBy = n.triggered_by?.name || n.triggered_by?.email || 'the lead owner';
                    const label = [n.lead_id_formatted, n.bank_name].filter(Boolean).join(' · ');
                    const detail = label
                        ? `${label}\n${n.rejection_reason || 'Claim auto-rejected'} by ${triggeredBy}.`
                        : `${n.rejection_reason || 'Claim auto-rejected'} by ${triggeredBy}.`;

                    // Stagger the toasts by 250ms so PrimeReact's toast
                    // stack renders them visibly distinct rather than as
                    // one collapsed block.
                    setTimeout(() => {
                        if (cancelled) return;
                        window.toast?.show({
                            severity: 'warn',
                            summary: 'Your claim was auto-rejected',
                            detail,
                            life: 8000,
                            sticky: false,
                        });
                    }, idx * 250);
                });

                // Mark all as seen. Fire-and-forget — if it fails, worst
                // case the user sees the same toasts again on next load,
                // which is fine.
                try {
                    await leadBroadcastService.markClaimRejectionsSeen(items.map((n) => n.id));
                    // Tell AppMenu to refresh the sidebar badge immediately.
                    window.dispatchEvent(new Event('notifications-refresh'));
                } catch (err) {
                    console.warn('Failed to mark claim rejections seen', err);
                }
            } catch (err) {
                // 401/403 expected if the user just logged out mid-fetch.
                // Anything else is logged but not surfaced — this is a
                // background nicety, not core functionality.
                console.warn('ClaimRejectionNotifier fetch failed', err);
            }
        })();

        return () => { cancelled = true; };
    }, []);

    return null;
};

export default ClaimRejectionNotifier;
